import axios from '../utils/axiosConfig';
import { CompanyPolicy, ApiResponse, ApiListResponse } from '../types/contentManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class CompanyPoliciesService {
  private baseURL = `${API_BASE_URL}/company-policies`;

  // Get all Company Policies
  async getAllPolicies(includeHidden: boolean = false, category?: string): Promise<CompanyPolicy[]> {
    try {
      let url = this.baseURL;
      const params = new URLSearchParams();
      
      if (includeHidden) params.append('includeHidden', 'true');
      if (category) params.append('category', category);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get<ApiListResponse<CompanyPolicy>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch company policies');
    } catch (error) {
      console.error('Error fetching company policies:', error);
      throw error;
    }
  }

  // Get visible Company Policies (for homepage)
  async getVisiblePolicies(category?: string): Promise<CompanyPolicy[]> {
    return this.getAllPolicies(false, category);
  }

  // Get policies by category
  async getPoliciesByCategory(category: string, includeHidden: boolean = false): Promise<CompanyPolicy[]> {
    return this.getAllPolicies(includeHidden, category);
  }

  // Get Company Policy by ID
  async getPolicyById(id: number): Promise<CompanyPolicy> {
    try {
      const response = await axios.get<ApiResponse<CompanyPolicy>>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch company policy');
    } catch (error) {
      console.error('Error fetching company policy:', error);
      throw error;
    }
  }

  // Create new Company Policy
  async createPolicy(formData: FormData): Promise<CompanyPolicy> {
    try {
      const response = await axios.post<ApiResponse<CompanyPolicy>>(this.baseURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create company policy');
    } catch (error) {
      console.error('Error creating company policy:', error);
      throw error;
    }
  }

  // Update Company Policy
  async updatePolicy(id: number, formData: FormData): Promise<CompanyPolicy> {
    try {
      const response = await axios.put<ApiResponse<CompanyPolicy>>(`${this.baseURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update company policy');
    } catch (error) {
      console.error('Error updating company policy:', error);
      throw error;
    }
  }

  // Delete Company Policy
  async deletePolicy(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.baseURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete company policy');
      }
    } catch (error) {
      console.error('Error deleting company policy:', error);
      throw error;
    }
  }

  // Toggle visibility
  async toggleVisibility(id: number): Promise<CompanyPolicy> {
    try {
      const response = await axios.patch<ApiResponse<CompanyPolicy>>(`${this.baseURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // Helper method to get file URL
  getFileUrl(filePath: string | undefined | null): string {
    if (!filePath || typeof filePath !== 'string') {
      return '';
    }
    
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    if (filePath.startsWith('/uploads/')) {
      return `${API_BASE_URL.replace('/api', '')}${filePath}`;
    }
    
    return `${API_BASE_URL.replace('/api', '')}/uploads/${filePath}`;
  }

  // Get icon for policy type
  getPolicyIcon(hasFile: boolean): string {
    return hasFile ? '📄' : '🔗';
  }

  // Get category badge color
  getCategoryColor(category: string): string {
    const colors: Record<string, string> = {
      'General': '#3498db',
      'HR': '#e74c3c',
      'IT': '#9b59b6',
      'Finance': '#2ecc71',
      'Operations': '#f39c12'
    };
    return colors[category] || '#95a5a6';
  }
}

export default new CompanyPoliciesService();
